import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { CatalogModalController } from '../../../catalog/shared/catalog-modal.controller';
import { CatalogDeleteConfirmModalComponent } from '../../../catalog/shared/components/catalog-delete-confirm-modal';
import { getErrorStatus, resolveApiErrorMessage } from '../../../shared/api-error';
import { PricingSupplementFilePicker } from './pricing-supplement-file-picker';
import { PricingSupplementService } from './pricing-supplement.service';
import { PricingSupplement, PricingSupplementMissingField } from './pricing-supplement.types';

type ToastVariant = 'success' | 'destructive' | 'warning' | 'info';
type CopyField = 'public' | 'document';

export interface PricingSupplementFlagChange {
  flag: 'executedDocsSignatureRemoved' | 'isPrivatePricingSupplement';
  checked: boolean;
}

export const PRICING_SUPPLEMENT_LINK_MODAL_ID = 'bond_detail_link_pricing_supplement_modal';
export const PRICING_SUPPLEMENT_DELETE_MODAL_ID = 'bond_detail_delete_pricing_supplement_modal';

/** Uses only the ProblemDetails payload so raw HttpErrorResponse messages never reach the user. */
function problemDetail(error: unknown, fallback: string): string {
  const payload = (error as { error?: unknown } | null)?.error;
  return resolveApiErrorMessage(fallback, { error: payload });
}

export function resolvePricingSupplementPublishError(error: unknown): string {
  switch (getErrorStatus(error)) {
    case 400:
      return problemDetail(error, "This trade has no Deal ID, so it can't be published automatically.");
    case 401:
      return 'Your session has expired. Sign in again and retry.';
    case 404:
      return problemDetail(error, 'This trade no longer exists.');
    case 500:
      return problemDetail(error, 'EzShare publishing is not configured (EzSharePublicDefaultPath). Contact an administrator.');
    case 502:
    case 503:
      return 'EzShare is unavailable right now. Try again in a few minutes.';
    default:
      return problemDetail(error, 'The document could not be published to EzShare.');
  }
}

export function resolvePricingSupplementLinkError(error: unknown): string {
  switch (getErrorStatus(error)) {
    case 400:
      return problemDetail(error, 'The document ID is not valid.');
    case 401:
      return 'Your session has expired. Sign in again and retry.';
    case 404:
      return problemDetail(error, 'Document not found in EzShare. Check the document ID.');
    case 409:
      return problemDetail(error, "The document is not Public in EzShare or its Identifier doesn't match the Deal ID.");
    case 502:
    case 503:
      return 'EzShare is unavailable right now. Try again in a few minutes.';
    default:
      return problemDetail(error, 'The document could not be linked.');
  }
}

@Component({
  selector: 'app-bond-pricing-supplement',
  imports: [DatePipe, NgTemplateOutlet, PricingSupplementFilePicker, CatalogDeleteConfirmModalComponent],
  templateUrl: './bond-pricing-supplement.html',
  styleUrl: './bond-pricing-supplement.css',
})
export class BondPricingSupplement implements OnChanges, OnDestroy {
  @Input({ required: true }) tradeId = '';
  @Input() executedDocsSignatureRemoved = false;
  @Input() isPrivate = false;
  @Input() flagsDisabled = false;
  /** Change this value to re-check the eligibility (e.g. after the bond detail is saved). */
  @Input() reloadKey: unknown = null;
  @Output() readonly flagChange = new EventEmitter<PricingSupplementFlagChange>();

  private readonly service = inject(PricingSupplementService);
  private readonly modals = new CatalogModalController();
  private readonly publishPicker = viewChild<PricingSupplementFilePicker>('publishPicker');
  private loadSubscription?: Subscription;

  protected readonly linkModalId = PRICING_SUPPLEMENT_LINK_MODAL_ID;
  protected readonly deleteModalId = PRICING_SUPPLEMENT_DELETE_MODAL_ID;

  protected readonly loading = signal(false);
  protected readonly loadError = signal('');
  protected readonly supplement = signal<PricingSupplement | null>(null);
  protected readonly canUpload = signal(true);
  protected readonly missingFields = signal<PricingSupplementMissingField[]>([]);
  protected readonly copiedField = signal<CopyField | null>(null);

  protected readonly selection = signal<File | null>(null);
  protected readonly publishing = signal(false);
  protected readonly publishError = signal('');

  protected readonly linkDocumentId = signal('');
  protected readonly linkSubmitted = signal(false);
  protected readonly linking = signal(false);
  protected readonly linkError = signal('');
  protected readonly linkDocumentIdError = computed(() =>
    this.linkSubmitted() && !this.linkDocumentId().trim() ? 'Document ID is required.' : '',
  );

  protected readonly deleting = signal(false);

  protected readonly displayName = computed(() => {
    const current = this.supplement();
    return current?.fileName || current?.ezShareDocumentId || 'Pricing supplement';
  });
  protected readonly deleteMessage = 'Remove this pricing supplement? The document will also be deleted from EzShare.';

  ngOnChanges(changes: SimpleChanges): void {
    const reloadChange = changes['reloadKey'];
    if (changes['tradeId'] || (reloadChange && !reloadChange.firstChange)) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  protected load(): void {
    const tradeId = this.tradeId.trim();
    this.loadSubscription?.unsubscribe();
    this.loadError.set('');
    if (!tradeId) {
      this.supplement.set(null);
      return;
    }

    this.loading.set(true);
    this.loadSubscription = this.service.getByTrade(tradeId).subscribe({
      next: (status) => {
        this.supplement.set(status.pricingSupplement);
        this.canUpload.set(status.canUploadPricingSupplement);
        this.missingFields.set(status.missingFields);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.supplement.set(null);
        this.loadError.set(
          getErrorStatus(error) === 404
            ? problemDetail(error, 'This trade was not found.')
            : problemDetail(error, 'The pricing supplement could not be loaded.'),
        );
        this.loading.set(false);
      },
    });
  }

  protected onFlagChange(flag: PricingSupplementFlagChange['flag'], checked: boolean): void {
    this.flagChange.emit({ flag, checked });
  }

  // Publish (primary flow)
  protected onSelectionChange(selection: File | null): void {
    this.selection.set(selection);
    this.publishError.set('');
  }

  protected publish(): void {
    const file = this.selection();
    if (!file || this.publishing() || this.isPrivate || !this.canUpload()) {
      return;
    }

    this.publishing.set(true);
    this.publishError.set('');
    this.service.uploadPublic(this.tradeId, file).subscribe({
      next: (supplement) => {
        this.supplement.set(supplement);
        this.selection.set(null);
        this.publishPicker()?.reset();
        this.publishing.set(false);
        this.toast('Pricing supplement uploaded to EzShare.', 'success');
      },
      error: (error: unknown) => {
        this.publishError.set(resolvePricingSupplementPublishError(error));
        this.publishing.set(false);
      },
    });
  }

  // Link existing EzShare document (secondary flow)
  protected openLinkModal(): void {
    this.linkDocumentId.set('');
    this.linkSubmitted.set(false);
    this.linkError.set('');
    this.modals.open(this.linkModalId);
  }

  protected submitLink(): void {
    this.linkSubmitted.set(true);
    const documentId = this.linkDocumentId().trim();
    if (!documentId || this.linking()) {
      return;
    }

    this.linking.set(true);
    this.linkError.set('');
    this.service.linkExisting(this.tradeId, { ezShareDocumentId: documentId }).subscribe({
      next: (supplement) => {
        this.supplement.set(supplement);
        this.linking.set(false);
        this.publishError.set('');
        this.modals.close(this.linkModalId);
        this.toast('EzShare document linked to this trade.', 'success');
      },
      error: (error: unknown) => {
        this.linkError.set(resolvePricingSupplementLinkError(error));
        this.linking.set(false);
      },
    });
  }

  // Remove
  protected openDeleteModal(): void {
    this.modals.open(this.deleteModalId);
  }

  protected confirmDelete(): void {
    if (this.deleting()) {
      return;
    }

    this.deleting.set(true);
    this.service.delete(this.tradeId).subscribe({
      next: () => {
        this.clearAfterDelete();
        this.toast('Pricing supplement removed.', 'success');
      },
      error: (error: unknown) => {
        if (getErrorStatus(error) === 404) {
          this.clearAfterDelete();
          this.toast('The pricing supplement was already removed.', 'warning');
          return;
        }
        this.deleting.set(false);
        this.toast(problemDetail(error, 'The pricing supplement could not be removed.'), 'destructive');
      },
    });
  }

  protected copy(field: CopyField, value: string): void {
    if (!value) {
      return;
    }

    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
    if (!clipboard?.writeText) {
      this.toast('Copy is not available in this browser.', 'warning');
      return;
    }

    clipboard.writeText(value).then(
      () => {
        this.copiedField.set(field);
        setTimeout(() => {
          if (this.copiedField() === field) {
            this.copiedField.set(null);
          }
        }, 1500);
      },
      () => this.toast('Could not copy to the clipboard.', 'warning'),
    );
  }

  private clearAfterDelete(): void {
    this.deleting.set(false);
    this.supplement.set(null);
    this.selection.set(null);
    this.publishError.set('');
    this.modals.close(this.deleteModalId);
  }

  private toast(message: string, variant: ToastVariant): void {
    window.KTToast?.show({ message, variant });
  }
}
